import React, { useEffect, useState } from "react";
import { ArrowRight, Star, Calculator, Stethoscope } from "lucide-react";
import { api } from "@/lib/api";

function goToCalc(tab) {
  return (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent("vp:set-calc-tab", { detail: { tab } }));
    setTimeout(() => {
      document.getElementById("kalkulatorji")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 30);
  };
}

export default function Hero() {
  const [avgRating, setAvgRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    api
      .get("/reviews")
      .then((res) => {
        if (!mounted) return;
        const list = res.data || [];
        setReviewCount(list.length);
        if (list.length > 0) {
          const sum = list.reduce((s, r) => s + (r.rating || 5), 0);
          setAvgRating(sum / list.length);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const fullStars = Math.round(avgRating);

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden"
      style={{ background: "var(--vp-bg)" }}
    >
      <div className="absolute inset-0 vp-grain opacity-60 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 md:pt-28 md:pb-28 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 vp-reveal">
            <div className="inline-flex items-center gap-2 rounded-full border border-[var(--vp-border)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--vp-muted)] mb-6">
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: "var(--vp-accent)" }}
              />
              Neodvisno svetovanje · 7+ let specializacije
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-[var(--vp-text)]">
              Zavarujte, kar
              <br />
              vam je{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-[var(--vp-primary)]">resnično</span>
                <span
                  aria-hidden
                  className="absolute left-0 right-0 bottom-1 h-3 -z-0"
                  style={{ background: "rgba(208,140,96,0.35)" }}
                />
              </span>{" "}
              pomembno.
            </h1>
            <p className="mt-6 text-lg text-[var(--vp-muted)] max-w-xl leading-relaxed">
              Osebno svetovanje za avtomobilska, zdravstvena in osebna zavarovanja,
              varčevanje za pokojnino in reševanje odškodninskih primerov. V slovenščini,
              brez zapletene terminologije.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#kontakt"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-white font-semibold transition-colors"
                style={{ background: "var(--vp-primary)" }}
              >
                Rezerviraj brezplačen posvet <ArrowRight size={18} />
              </a>
              <a
                href="#kalkulatorji"
                onClick={goToCalc("premium")}
                data-testid="hero-cta-premium"
                className="inline-flex items-center gap-2 rounded-full px-7 py-4 font-semibold border border-[var(--vp-border)] bg-white text-[var(--vp-text)] hover:bg-[var(--vp-sand)] transition-colors"
              >
                <Calculator size={16} /> Izračun premije
              </a>
              <a
                href="#kalkulatorji"
                onClick={goToCalc("auto")}
                data-testid="hero-cta-claim"
                className="inline-flex items-center gap-2 rounded-full px-7 py-4 font-semibold border border-[var(--vp-border)] bg-white text-[var(--vp-text)] hover:bg-[var(--vp-sand)] transition-colors"
              >
                <Stethoscope size={16} /> Izračun odškodnine
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-[var(--vp-muted)]">
              {reviewCount > 0 ? (
                <div className="flex items-center gap-2" data-testid="hero-rating">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star
                        key={i}
                        size={14}
                        fill={i <= fullStars ? "#D08C60" : "transparent"}
                        stroke="#D08C60"
                      />
                    ))}
                  </div>
                  <span className="font-semibold text-[var(--vp-text)]">
                    {avgRating.toFixed(1)}/5
                  </span>
                  <span>· iz {reviewCount} mnenj strank</span>
                </div>
              ) : (
                <div
                  data-testid="hero-no-rating"
                  className="font-semibold text-[var(--vp-text)]"
                >
                  450+ zadovoljnih strank
                </div>
              )}
              <div className="h-4 w-px bg-[var(--vp-border)]" />
              <div>Neodvisni nasveti, dolgoročno sodelovanje</div>
            </div>
          </div>

          <div className="lg:col-span-5 vp-reveal" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              <div
                className="absolute -inset-4 rounded-[2rem] -z-10"
                style={{ background: "var(--vp-sand)" }}
              />
              <img
                src="https://images.unsplash.com/photo-1588979355313-6711a095465f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBvdXRkb29ycyUyMGhhcHB5fGVufDB8fHx8MTc4MDc2MDA4OXww&ixlib=rb-4.1.0&q=85"
                alt="Družina"
                className="rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] w-full h-[460px] object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
